import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nfmt, usd, credits } from "@/lib/adminMeta";
import { Sparkles, Building2, Cpu, Calendar, DollarSign, Bell, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div className={`bg-white rounded-2xl border p-5 ${accent ? "border-gold/60" : "border-border"}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-sm">
      <Icon className="w-4 h-4 text-gold" /> {label}
    </div>
    <div className="text-3xl font-bold text-navy mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

function BudgetAlerts({ budget, rates, isOwner, onSaved }) {
  const pad = (arr) => { const a = [...(arr || [])]; while (a.length < 3) a.push(""); return a.slice(0, Math.max(3, a.length)); };
  const [enabled, setEnabled] = useState(budget.enabled);
  const [vals, setVals] = useState(pad(budget.thresholds));
  const [saving, setSaving] = useState(false);
  const exceeded = new Set((budget.exceeded || []).map(Number));

  const save = async () => {
    setSaving(true);
    try {
      const thresholds = vals.map((v) => parseFloat(v)).filter((n) => !isNaN(n) && n > 0);
      const { data } = await api.patch("/admin/budget", { enabled, thresholds });
      toast.success("Budget alerts updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update budget alerts");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid="budget-alerts">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-semibold text-navy flex items-center gap-2"><Bell className="w-4 h-4 text-gold" /> Monthly budget alerts</h2>
        <span className="text-xs text-muted-foreground">Spend this month: <strong className="text-navy">{usd(budget.month_spend_usd)}</strong></span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Soft alerts on estimated AI spend. These do not block generation.</p>

      {budget.enabled && (budget.exceeded || []).length > 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="budget-exceeded-banner">
          <AlertTriangle className="w-4 h-4" /> This month's estimated spend has passed {(budget.exceeded).map((t) => usd(t)).join(", ")}.
        </div>
      )}

      {isOwner ? (
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium text-navy cursor-pointer" data-testid="budget-enabled-toggle">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-navy" />
            Alerts enabled
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            {vals.map((v, i) => (
              <div key={i} className="space-y-1.5">
                <Label className="text-xs">Threshold {i + 1} (USD)</Label>
                <Input type="number" min="0" step="1" value={v} placeholder="e.g. 25" disabled={!enabled}
                  onChange={(e) => setVals((s) => s.map((x, j) => (j === i ? e.target.value : x)))}
                  data-testid={`budget-threshold-${i}`} />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Leave a field blank to remove that threshold. Turn off the toggle to disable all alerts.</p>
          <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white font-bold" data-testid="budget-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save alerts"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="budget-readonly">
          {(budget.thresholds || []).length === 0 ? (
            <span className="text-sm text-muted-foreground">No thresholds configured.</span>
          ) : budget.thresholds.map((t) => (
            <span key={t} className={`text-sm font-semibold px-3 py-1 rounded-full border ${exceeded.has(Number(t)) && budget.enabled ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-secondary/60 text-navy border-border"}`}>{usd(t)}</span>
          ))}
          {!budget.enabled && <span className="text-xs text-muted-foreground self-center">(alerts disabled)</span>}
        </div>
      )}
    </div>
  );
}

export default function AdminUsage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/usage").then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const p = data?.platform;
  const rows = data?.by_dealership || [];
  const isOwner = user?.role === "owner";

  return (
    <div className="space-y-6 fade-up" data-testid="admin-usage">
      <div>
        <h1 className="text-3xl font-bold text-navy">Usage & Cost</h1>
        <p className="text-muted-foreground mt-1">Estimated AI campaign generation cost across the platform.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={DollarSign} accent label={`Platform AI cost (${data?.month_label || "this month"})`}
          value={loading ? "…" : usd(p?.this_month?.cost?.total_usd)} sub={loading ? "" : `${credits(p?.this_month?.cost?.credits)} · ${nfmt(p?.this_month?.generations)} generations`} testid="usage-cost-month" />
        <StatCard icon={DollarSign} label="Platform AI cost (all time)"
          value={loading ? "…" : usd(p?.all_time?.cost?.total_usd)} sub={loading ? "" : credits(p?.all_time?.cost?.credits)} testid="usage-cost-all" />
        <StatCard icon={Sparkles} label="Total campaign generations"
          value={loading ? "…" : nfmt(p?.all_time?.generations)} sub="all time" testid="usage-total-generations" />
        <StatCard icon={Cpu} label="Total token usage"
          value={loading ? "…" : nfmt(p?.all_time?.total_tokens)} sub={loading ? "" : `${nfmt(p?.all_time?.input_tokens)} in · ${nfmt(p?.all_time?.output_tokens)} out`} testid="usage-total-tokens" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard icon={Cpu} label="Input token cost (all time)"
          value={loading ? "…" : usd(p?.all_time?.cost?.input_usd)} sub={loading ? "" : `${nfmt(p?.all_time?.input_tokens)} tokens @ ${usd(data?.rates?.input_usd_per_1m)}/1M`} testid="usage-input-cost" />
        <StatCard icon={Cpu} label="Output token cost (all time)"
          value={loading ? "…" : usd(p?.all_time?.cost?.output_usd)} sub={loading ? "" : `${nfmt(p?.all_time?.output_tokens)} tokens @ ${usd(data?.rates?.output_usd_per_1m)}/1M`} testid="usage-output-cost" />
      </div>

      {data?.budget && (
        <BudgetAlerts budget={data.budget} rates={data.rates} isOwner={isOwner}
          onSaved={(b) => setData((s) => ({ ...s, budget: { ...s.budget, ...b } }))} />
      )}

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gold" />
          <h2 className="font-semibold text-navy">Estimated AI cost by dealership</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Dealership", "Generations (all time)", "Tokens (all time)", `Cost (${data?.month_label || "this month"})`, "Cost (all time)"].map((h) => (
                  <th key={h} className="px-5 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="usage-table-body">
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No campaign generations recorded yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.dealership_id} onClick={() => navigate(`/admin/usage/dealers/${r.dealership_id}`)}
                  className="hover:bg-secondary/40 cursor-pointer" data-testid={`usage-row-${r.dealership_id}`}>
                  <td className="px-5 py-3 font-medium text-navy whitespace-nowrap">{r.name}</td>
                  <td className="px-5 py-3">{nfmt(r.all_time.generations)}</td>
                  <td className="px-5 py-3 text-muted-foreground">{nfmt(r.all_time.total_tokens)}</td>
                  <td className="px-5 py-3 font-semibold">{usd(r.this_month.cost.total_usd)}</td>
                  <td className="px-5 py-3 font-semibold text-navy">{usd(r.all_time.cost.total_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground" data-testid="usage-estimate-note">
        These are calculated estimates based on the currently configured Emergent Universal Key rates
        ({data?.rates ? `${usd(data.rates.input_usd_per_1m)}/1M input, ${usd(data.rates.output_usd_per_1m)}/1M output, 1 credit = ${usd(data.rates.credit_usd)}` : "loading"}).
        Actual charges may differ if provider pricing changes.
      </p>
    </div>
  );
}
